import { z } from '@hono/zod-openapi'

export const FormTypeSchema = z.enum([
  'iep-cover', 'iep-present-levels', 'iep-goals', 'iep-services',
  'iep-transition', 'iep-placement', 'eval-consent', 'eval-report',
  's504-plan', 'mtss-referral', 'health-plan', 'pwn',
  'eligibility', 'manifestation',
]).openapi({ example: 'iep-cover', description: 'PRD form type matching the monolith registry' })

export const FormFieldDescriptorSchema = z.object({
  id: z.string().openapi({ example: 'student.name' }),
  type: z.enum(['text', 'textarea', 'select', 'checkbox', 'radio', 'date', 'signature', 'file', 'calculated']),
  label: z.string(),
  required: z.boolean(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).nullable(),
  defaultValue: z.unknown().nullable(),
  validation: z.object({
    minLength: z.number().int().nullable(),
    maxLength: z.number().int().nullable(),
    pattern: z.string().nullable(),
    min: z.number().nullable(),
    max: z.number().nullable(),
  }).nullable(),
}).openapi('FormFieldDescriptor')

export const FormSectionDescriptorSchema = z.object({
  id: z.string().openapi({ example: 'student-info' }),
  title: z.string(),
  fields: z.array(FormFieldDescriptorSchema),
  repeatable: z.boolean().default(false),
  condition: z.string().nullable().openapi({ description: 'Conditional display expression' }),
}).openapi('FormSectionDescriptor')

export const FormDescriptorSchema = z.object({
  formType: FormTypeSchema,
  title: z.string(),
  lane: z.enum(['mtss', 'sped', 's504', 'el', 'health']),
  sections: z.array(FormSectionDescriptorSchema),
  allowSignatures: z.boolean(),
  expiresInDays: z.number().int().default(14),
  provider: z.string().nullable().openapi({ description: 'Default external provider' }),
}).openapi('FormDescriptor', { description: 'Form template descriptor from PRD_FORMS' })

export const FormValuesSchema = z.record(z.string(), z.unknown()).openapi({
  description: 'Flat key-value map of form field values (keyed by field ID)',
})

export const FormDispatchRequestSchema = z.object({
  districtId: z.string().min(1).max(64),
  formType: FormTypeSchema,
  studentId: z.string().uuid().nullable(),
  recipientEmail: z.string().email(),
  recipientName: z.string().max(255).nullable(),
  payload: FormValuesSchema,
  expiresInDays: z.number().int().nullable(),
  provider: z.string().nullable(),
  message: z.string().max(5000).nullable(),
}).openapi('FormDispatchRequest')

export const FormDispatchResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('sent'),
  expiresAt: z.string().datetime(),
  providerRef: z.string().nullable(),
}).openapi('FormDispatchResponse')

export const FormResponseWebhookSchema = z.object({
  provider: z.string(),
  providerRef: z.string(),
  dispatchId: z.string().uuid(),
  responses: FormValuesSchema,
  completedAt: z.string().datetime(),
  signature: z.string().nullable(),
}).openapi('FormResponseWebhook', { description: 'Incoming form response from external provider' })

export type FormType = z.infer<typeof FormTypeSchema>
export type FormFieldDescriptor = z.infer<typeof FormFieldDescriptorSchema>
export type FormSectionDescriptor = z.infer<typeof FormSectionDescriptorSchema>
export type FormDescriptor = z.infer<typeof FormDescriptorSchema>
export type FormDispatchRequest = z.infer<typeof FormDispatchRequestSchema>
export type FormDispatchResponse = z.infer<typeof FormDispatchResponseSchema>
export type FormResponseWebhook = z.infer<typeof FormResponseWebhookSchema>
